import { useState } from 'react';
import { callFn } from '../lib/api';
import { PAUSE_DURATION_LABEL, WALL_CLOCK_DURATIONS, resumesLabel } from '../lib/types';
import type { PauseChannel, PauseDuration, StorePause } from '../lib/types';

const DURATIONS: PauseDuration[] = ['1h', '2h', 'rest_of_today', 'tomorrow_morning', 'indefinite'];

interface Props {
  channel: PauseChannel;
  /** What is being paused, in the owner's words: "delivery", "pickup", "all ordering". */
  noun: string;
  placeholder: string;
  pause: StorePause | undefined;
  /** Set once the store has a time zone; gates the wall-clock durations. */
  timezone: string | null;
  onDone: () => Promise<void> | void;
  toast: (msg: string, kind?: 'ok' | 'err') => void;
}

/**
 * Pause a channel with a note customers will read (PLAN_STOREFRONT_DELIVERY D-P2).
 *
 * The duration is sent by NAME, never as a timestamp: "until tomorrow morning"
 * has to mean 8am at the STORE, and this browser is wherever the owner happens
 * to be standing. The two wall-clock options are disabled until the store has a
 * time zone rather than silently resolved in some other one.
 */
export function PauseControl({ channel, noun, placeholder, pause, timezone, onDone, toast }: Props) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [duration, setDuration] = useState<PauseDuration>('rest_of_today');
  const [busy, setBusy] = useState(false);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); toast(label, 'ok'); setOpen(false); setNote(''); await onDone(); }
    catch (e) { toast(e instanceof Error ? e.message : 'That did not work.', 'err'); }
    finally { setBusy(false); }
  };

  if (pause) {
    const back = resumesLabel(pause.resumesAt);
    return (
      <div className="callout">
        <p>
          <strong>{noun[0].toUpperCase() + noun.slice(1)} is paused.</strong>{' '}
          {pause.note || 'Customers are told it is paused.'}{' '}
          <span className="muted">{back ? `Resumes automatically — ${back}.` : 'Until you turn it back on.'}</span>
        </p>
        <button className="btn btn-sm" disabled={busy}
                onClick={() => void run(`${noun} resumed`, () => callFn('resume_fulfillment', { channel }))}>
          Resume now
        </button>
      </div>
    );
  }

  if (!open) {
    return <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>Temporarily pause {noun}</button>;
  }

  const wallClockBlocked = !timezone;
  const chosenBlocked = wallClockBlocked && WALL_CLOCK_DURATIONS.includes(duration);

  return (
    <div className="subcard">
      <h3>Pause {noun}</h3>
      <label>What should customers see?
        <input className="input" maxLength={200} placeholder={placeholder}
               value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <label>For how long?
        <select className="input" style={{ maxWidth: 260 }} value={duration}
                onChange={(e) => setDuration(e.target.value as PauseDuration)}>
          {DURATIONS.map((d) => (
            <option key={d} value={d} disabled={wallClockBlocked && WALL_CLOCK_DURATIONS.includes(d)}>
              {PAUSE_DURATION_LABEL[d]}
            </option>
          ))}
        </select>
      </label>
      {wallClockBlocked && (
        <p className="muted small">
          &ldquo;Rest of today&rdquo; and &ldquo;until tomorrow morning&rdquo; need your store&rsquo;s time zone &mdash; set it in the General section above.
        </p>
      )}
      <p className="muted small">It resumes on its own. You can always resume it sooner.</p>
      <div className="row wrap" style={{ gap: 8 }}>
        <button className="btn" disabled={busy || chosenBlocked}
                onClick={() => void run(`${noun} paused`, () => callFn('pause_fulfillment', { channel, note: note.trim(), duration }))}>
          {busy ? 'Pausing…' : `Pause ${noun}`}
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}
